import React, {useState, useEffect, useRef} from 'react';
import {
  ScrollView,
  View,
  Image,
  ImageBackground,
  Text,
  Animated,
  Pressable,
} from 'react-native';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import RecordList from '../Shared/RecordList';
import QuickAddList from '../Shared/QuickAddList';
import {Submission} from '../../Models/PowerSyncModels/models';
import {SubmissionStatuses} from '../../Constants/SubmissionStatuses';
import {TemplateCategoryTypes} from '../../Constants/TemplateCategoryTypes';
import {HomeStyleSheet} from '../../Styles/HomeStyles';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Navigation} from '../../Constants/Navigation';
import Colors from '../../Styles/Shared/Colors';
import {Images} from '../../Constants/Images';
import {
  METADATA_KEYS,
  COMMON_TEMPLATES,
  CATEGORY,
  PERMISSIONS,
} from '../../Constants/AppConstants';
import {useIsFocused} from '@react-navigation/native';
import {usePermission} from '../../Providers/PermissionProvider';
import {useRelation} from '../../Providers/SubmissionRelationProvider';
import {FilterTypes} from '../../Constants/General';
import ScreenButtons from '../Shared/ScreenButtons';
import {usePowerSyncUser} from '../../Providers/PowerSyncUserProvider';
import {FilterValue} from '../Shared/SearchBox';
import LoadingSpinner from '../Shared/LoadingSpinner';

export declare type DashboardProps = {
  navigation: any;
};

const Dashboard = (props: DashboardProps): React.ReactElement => {
  const [reports, setReports] = useState<Submission[]>();
  const [filterValue, setFilterValue] = useState<FilterValue>({
    startDate: '',
    endDate: '',
    statuses: [],
    types: [],
    person: '',
    project: '',
    equipmentType: '',
    equipment: '',
    pendingInvestigation: false,
    investigationAttached: false,
  });

  const [headerImage, setHeaderImage] = useState(Images.GOOD_MORNING);
  const [headerText, setHeaderText] = useState('');
  const isFocused = useIsFocused();

  const opacityHeader = useRef(new Animated.Value(0)).current;

  const {templateCategories, templates, submissions, people, powersyncReady} = usePowerSyncData();
  const user = usePowerSyncUser();
  const {hasPermission} = usePermission();
  const {getRelatedSubmissions} = useRelation();

  const [isLoading, setIsLoading] = useState(true);

  const unselectCommand = useRef({
    unselect: () => {},
    setUnselectCommand(func: () => void) {
      this.unselect = func;
    },
  });

  useEffect(() => {
    const fetchAsync = async () => {
      if (submissions.length > 0 && people.length > 0) {
        await fetchList();
      } else {
        setIsLoading(false);
      }
    };

    fetchAsync();
  }, [powersyncReady]);

  useEffect(() => {
    const fetchAsync = async () => {
      if (submissions.length > 0 && people.length > 0 && isFocused) {
        await fetchList();
      }
    };
    fetchAsync();
  }, [isFocused]);

  const fetchList = async () => {
    let viewReport = hasPermission(PERMISSIONS.TASK_VIEW);

    let templateCategory = templateCategories
      .find(t => t.name === TemplateCategoryTypes.HOME);
      
    let listTemplateIds = templateCategory?.listTemplateIds ?? [];

    let filteredTemplates = templates
      .filter(
        t =>
          listTemplateIds.includes(t._id)
      )
      .map(t => t.name.toLowerCase().trim());

    let filteredSubmissions: Submission[] = [];
    if (viewReport) {
      filteredSubmissions = submissions.filter(s =>
        filteredTemplates.includes(s.templateVersion.name?.toLowerCase().trim() ?? ''),
      );
    }
    //If no view permissions can only see forms that user created or that they can manage
    else {
      //Special permissions for PM/Supervisor
      let userInfo = user.getUserInfo();
      if (user) {
        let userEmail = userInfo.email;
        let personLoggedIn = people.find(x => x.email == userEmail);

        if (personLoggedIn) {
          let submissionIds = await getRelatedSubmissions(personLoggedIn._id);
          filteredSubmissions = submissions.filter(
            x => submissionIds.includes(x._id) || x.createdBy == userInfo.email,
          );
        }
      }
    }

    setReports(filteredSubmissions);
    setIsLoading(false);
  };

  useEffect(() => {
    const hours = new Date().getHours();

    if (hours >= 5 && hours < 12) {
      setHeaderImage(Images.GOOD_MORNING);
      setHeaderText('Good morning');
    } else if (hours >= 12 && hours < 18) {
      setHeaderImage(Images.GOOD_AFTERNOON);
      setHeaderText('Good afternoon');
    } else {
      setHeaderImage(Images.GOOD_EVENING);
      setHeaderText('Good evening');
    }

    Animated.timing(opacityHeader, {
      toValue: 1,
      duration: 200,
      useNativeDriver: true,
    }).start();
  }, []);

  if (!isFocused) return <></>;

  function recordPressed(recordId: string): void {
    let task = submissions.find(s => s._id == recordId);

    if (task?.submissionStatus.name == SubmissionStatuses.DRAFT)
      props.navigation.navigate(Navigation.DYNAMICFORM, {
        submissionId: recordId,
        title: 'Task',
        showConfirmation: false,
      });
    else
      props.navigation.navigate(Navigation.COMPLETIONFORM, {
        submissionId: recordId,
        title: 'Task',
      });
  }

  return (
    <React.Fragment>
      <ScrollView style={{backgroundColor: Colors.white}}>
        <Pressable
          style={{
            //@ts-ignore
            cursor: 'auto',
          }}
          onPress={() => {
            unselectCommand.current.unselect();
          }}>
          <>
            <Animated.View style={{opacity: opacityHeader}}>
              <ImageBackground
                source={headerImage}
                resizeMode="cover"
                style={{height: 260}}>
                <Text style={HomeStyleSheet.headerTitle}>{headerText}</Text>
              </ImageBackground>
            </Animated.View>
            <View style={{flexDirection: 'row'}}>
              <View style={HomeStyleSheet.tasksList}>
                <RecordList
                  records={reports}
                  title={'Tasks'}
                  category={CATEGORY.HOME}
                  showDrafts={true}
                  showAssignedTo={true}
                  hasMetaData={true}
                  metaDataKey_title={METADATA_KEYS.TITLE}
                  showFilters={true}
                  filters={[
                    {label: 'Due Date Range', type: FilterTypes.DATE_RANGE},
                    {label: '', type: FilterTypes.TASK_STATUS},
                  ]}
                  navigation={props.navigation}
                  onRecordPressed={recordId => recordPressed(recordId)}
                  setUnselectCommand={(func: () => void) => {
                    unselectCommand.current.setUnselectCommand(func);
                  }}
                  filterValue={filterValue}
                  onChangeFilter={filterValue => setFilterValue(filterValue)}
                />
              </View>
              <View style={{flex: 1}}>
                <ScreenButtons
                  navigation={props.navigation}
                  category={TemplateCategoryTypes.HOME}
                />
                <View style={CommonStyleSheet.bcsfFooter}>
                  <Image
                    source={Images.BCSF}
                    style={CommonStyleSheet.bcsfFooterImage}
                  />
                  <Text style={CommonStyleSheet.bcsfFooterText}>
                    BC <Text style={{fontWeight: '700'}}>Forest Safety</Text>
                  </Text>
                </View>
              </View>
            </View>
          </>
        </Pressable>
      </ScrollView>
      <QuickAddList navigation={props.navigation} />
      <LoadingSpinner message="Loading" visible={isLoading} />
    </React.Fragment>
  );
};

export default Dashboard;
